import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const MWST_OPTIONS = [
  { value: '8.1', label: '8.1%' },
  { value: '2.6', label: '2.6%' },
  { value: '0', label: '0%' },
];

/**
 * Dialog shown when the reviewer wants to promote a free article into the catalog.
 * Pre-fills name, unit, MWST and price from the line, lets the reviewer adjust the
 * standard catalog price (this becomes the new item's `standard_price_chf`).
 *
 * Props:
 *   open, onOpenChange
 *   line   — the work-order line being promoted (free article)
 *   onConfirm({ name, unit, mwst_rate, standard_price_chf, category })
 */
export function PromoteToCatalogDialog({ open, onOpenChange, line, onConfirm }) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [mwst, setMwst] = useState('8.1');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    if (open && line) {
      setName(line.item_name || '');
      setUnit(line.item_unit || '');
      setMwst(line.mwst_rate_snapshot != null ? String(line.mwst_rate_snapshot) : '8.1');
      setPrice(line.assigned_unit_price_chf != null ? String(line.assigned_unit_price_chf) : '');
      setCategory('');
    }
  }, [open, line]);

  const canSubmit = name.trim().length > 0
    && unit.trim().length > 0
    && !isNaN(parseFloat(price));

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm?.({
      name: name.trim(),
      unit: unit.trim(),
      mwst_rate: parseFloat(mwst),
      standard_price_chf: parseFloat(price),
      category: category.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="promote-to-catalog-dialog">
        <DialogHeader>
          <DialogTitle>Salva nel catalogo</DialogTitle>
          <DialogDescription>
            L'articolo libero diventerà permanente nel catalogo e sarà disponibile
            per i prossimi bollettini.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="promo-name">Nome articolo *</Label>
            <Input id="promo-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="promo-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="promo-unit">Unità *</Label>
              <Input id="promo-unit" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="promo-unit" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo-mwst">IVA *</Label>
              <Select value={mwst} onValueChange={setMwst}>
                <SelectTrigger id="promo-mwst" data-testid="promo-mwst">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MWST_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="promo-price">Prezzo standard (CHF) *</Label>
            <Input
              id="promo-price"
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              data-testid="promo-price"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="promo-category">Categoria (opzionale)</Label>
            <Input id="promo-category" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="promo-category" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="promo-cancel">Annulla</Button>
          <Button onClick={handleConfirm} disabled={!canSubmit} data-testid="promo-confirm">Salva nel catalogo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
